// Pure formatting/scheduling helpers for c-sidebar-location.vue. Deliberately independent of
// `src/modules/admin/subapps/suite/screens/locations/hours/helpers.ts` — that module is fine to
// lazy-import for the manual-override modal, but this file's exports are pulled into the app shell
// (the dock renders on every ZynoWorkstation route) and must not drag the admin hours-editor code
// along with it.

import type { LocationHoursStateResponse } from '@/openapi-clients-generated/suite';

export type DotTone = 'ok' | 'busy' | 'idle';

/** `open`/`closed` -> the shared status-dot tone; no resolved state at all reads as idle. */
export function dotToneForState(state: Pick<LocationHoursStateResponse, 'currentResolvedState'> | undefined | null): DotTone {
    if (!state) return 'idle';
    return state.currentResolvedState === 'open' ? 'ok' : 'busy';
}

/**
 * A location has a real status worth showing once its state row exists — even with no hours and no
 * active override, it's effectively closed rather than "unconfigured" — mirrors the admin screen's
 * `isLocationConfigured`, reimplemented here to avoid importing it (see file header).
 */
export function isConfiguredForDisplay(state: Pick<LocationHoursStateResponse, 'currentResolvedSource'> | undefined | null): boolean {
    return Boolean(state);
}

/** An ISO instant in a location's timezone: `"2:00 PM"` today, `"Tue 2:00 PM"` on another day. */
export function formatLocationTime(iso: string, tz: string | null, now: Date = new Date()): string {
    const zone = tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = new Date(iso);
    const dayKeyFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' });
    const time = new Intl.DateTimeFormat(undefined, { timeZone: zone, hour: 'numeric', minute: '2-digit' }).format(date);
    if (dayKeyFormatter.format(date) === dayKeyFormatter.format(now)) return time;
    const weekday = new Intl.DateTimeFormat(undefined, { timeZone: zone, weekday: 'short' }).format(date);
    return `${weekday} ${time}`;
}

/** The dock row's sub-line for an active manual override. */
export function manualOverrideSubLine(
    state: Pick<LocationHoursStateResponse, 'manualStatus' | 'manualExpiresAt'>,
    tz: string | null,
    now: Date = new Date()
): string {
    const verb = state.manualStatus === 'open' ? 'Open' : 'Closed'; // the row's "Manual" badge says it's an override
    return state.manualExpiresAt ? `${verb} · until ${formatLocationTime(state.manualExpiresAt, tz, now)}` : `${verb} · until cleared`;
}

/** The dock row's sub-line for a resolved state. `policyConfigured` is the bound policy's own "has hours" flag
 * (independent of any override) — a no-hours, no-override location still gets a real Open/Closed line, tagged
 * to explain why it's closed by default. */
export function resolvedSubLine(
    state: LocationHoursStateResponse,
    policyConfigured: boolean | undefined,
    tz: string | null,
    now: Date = new Date()
): string {
    if (state.currentResolvedSource === 'manual_override') return manualOverrideSubLine(state, tz, now);
    const base = state.currentResolvedState === 'open' ? 'Open' : 'Closed';
    if (state.currentResolvedSource === 'exception') return `${base} · Special hours`;
    return policyConfigured ? base : `${base} · No schedule set`;
}

const BOUNDARY_BUFFER_MS = 75_000;
const RETRY_DELAY_MS = 60_000;
const MAX_DELAY_MS = 6 * 60 * 60 * 1000;

/**
 * When the dock row should next re-fetch on its own, ahead of the 5-minute safety poll: shortly
 * after the sooner of the manual override's expiry or the next scheduled transition. If that
 * moment has already passed (clock drift, a missed SSE event), retry soon rather than waiting out
 * the full boundary window again. `null` means no boundary is known — rely on the safety poll/SSE.
 */
export function computeNextRefreshDelayMs(
    state: Pick<LocationHoursStateResponse, 'manualExpiresAt' | 'nextResolveAt'> | undefined | null,
    now: number = Date.now()
): number | null {
    if (!state) return null;
    const boundaries = [state.manualExpiresAt, state.nextResolveAt].filter((iso): iso is string => iso != null).map(iso => new Date(iso).getTime());
    if (!boundaries.length) return null;
    const delay = Math.min(...boundaries) - now + BOUNDARY_BUFFER_MS;
    if (delay <= 0) return RETRY_DELAY_MS;
    return Math.min(delay, MAX_DELAY_MS);
}
